import {
    IonButton,
    IonCard,
    IonContent,
    IonItem,
    IonList,
    IonPage,
    IonRadio,
    IonRadioGroup,
    IonText,
    IonTextarea,
    useIonAlert,
    useIonRouter,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import { HeaderComponent } from "../../../../components";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { trackEvent } from "../../../../services/analytics/analyticsService";
import { Capacitor } from "@capacitor/core";
import {
    useCurrentStripePlan,
    useProfile,
    useStripeSubscription,
} from "../../../../components/hooks";
import { useParams } from "react-router";
import { routes } from "../../../../route/constants";
import { updateUserActionsCheckList } from "../../../../services/userActionsCheckListService";
import { store } from "../../../../redux/store";
import { Reasons, Flow } from "./cancelFlow";
import { asPlanType } from "../../../../services/StripeService";
import { isFeatureAvailableCurrentLanguage } from "../../../../utils/FeatureToggle";
import * as Sentry from "@sentry/react";

export const CancelSubscription: FC<{}> = () => {
    const router = useIonRouter();
    const { t } = useTranslation();
    const profile = useProfile();
    const currentPlan = useCurrentStripePlan();
    const sub = useStripeSubscription();
    const [displayErrorAlert] = useIonAlert();

    const { provider } = useParams<{ provider: string }>();
    const userActionsCheckList = store.getState().userActionsCheckList;

    const [feedbackText, setFeedbackText] = useState<string>("");
    const [cancellationReason, setCancellationReason] = useState<
        keyof typeof Reasons | null
    >();
    const [showFlow, setShowFlow] = useState<boolean>(false);
    useEffect(() => {
        if (!userActionsCheckList.visitedCancellationFlow) {
            updateUserActionsCheckList({
                visitedCancellationFlow: true,
            });
        }
    }, []);

    const handleDisableCancelButton = useMemo(() => {
        if (
            cancellationReason === t("cancellation.reasons.other") &&
            feedbackText.length <= 0
        ) {
            return true;
        } else if (!cancellationReason) {
            return true;
        }

        return false;
    }, [cancellationReason, feedbackText, t]);
    useEffect(() => {
        Sentry.configureScope((scope) => {
            console.log(sub);
            scope.setTags({
                provider: provider,
                cancellationReason: cancellationReason,
                currentPlan: !!currentPlan,
                currentPlanName: sub?.planName || "unknown",
            });
        });
    }, [
        provider,
        cancellationReason,
        currentPlan,
        t,
        profile,
        userActionsCheckList.visitedCancellationFlow,
        sub,
    ]);
    const hasGoogle = provider === "google";
    const hasApple = provider === "apple";
    const hasStripe = provider === "stripe";

    const sendMailToSupport = () => {
        // Send email to support
        const subject = "Cancel Subscription";
        let body =
            "Username: " +
            profile?.username +
            "\nReason: " +
            "" +
            "\nSubscription: ";
        if (feedbackText && feedbackText.length > 0) {
            body = feedbackText + "\n\n" + body;
        }

        body += hasStripe ? "Website " : "";
        body += hasGoogle ? "PlayStore " : "";
        body += hasApple ? "AppStore " : "";
        body = body.trim() + "\n\n";

        const url = `mailto:contact@refsix.com?subject=${encodeURIComponent(
            subject
        )}&body=${encodeURIComponent(body)}`;
        window.open(url, "_system");
    };

    const cancelSubscriptionHandle = useCallback(async () => {
        trackEvent("CancellationReason", {
            reason: cancellationReason,
            reasonCode: t(cancellationReason!),
            reasonText: feedbackText,
            hasGoogleSubscription: hasGoogle,
            hasAppleSubscription: hasApple,
            hasStripeSubscription: hasStripe,
            hasInAppPurchase: hasApple || hasGoogle,
        });

        if (Capacitor.getPlatform() === "android" && hasGoogle) {
            window.open(
                "https://play.google.com/store/account/subscriptions",
                "_system"
            );
        } else if (Capacitor.getPlatform() === "ios" && hasApple) {
            window.open(
                "itms-apps://apps.apple.com/account/subscriptions",
                "_system"
            );
        } else if (
            hasStripe &&
            isFeatureAvailableCurrentLanguage("cancellationFlows")
        ) {
            if (!currentPlan) {
                Sentry.captureMessage("Users existing Stripe plan not found");
                trackEvent("CancelFlowError", {
                    cancellationReason,
                    plan: sub?.planName || "unknown",
                    provider,
                    errorMessage: "Users existing Stripe plan not found",
                });
                await displayErrorAlert({
                    header: t("feedback.feedback.failure.title"),
                    //message: t("general.errorTryAgain"),
                    buttons: [
                        {
                            text: t("cancellation.contactSupport"),
                            role: "confirm",
                            handler: () => {
                                sendMailToSupport();
                                router.push(routes.refsixFixtures, "root");
                            },
                        },
                        {
                            text: t("general.cancel"),
                            role: "cancel",
                            handler: () => {
                                setShowFlow(false);
                                router.push(routes.refsixFixtures, "root");
                            },
                        },
                    ],
                });
            } else {
                setShowFlow(true);
            }
        } else {
            sendMailToSupport();

            router.push(routes.refsixFixtures, "root");
        }
    }, [
        cancellationReason,
        feedbackText,
        profile,
        provider,
        router,
        currentPlan,
        t,
    ]);

    return (
        <IonPage>
            <HeaderComponent
                showBackButton
                title={t("myaccount.cancelSubscription")}
                titleTestId="reason-cancel-subscription-header"
            />
            <IonContent>
                {currentPlan === undefined ? (
                    <>
                        <h1>{t("general.errorTryAgain")}</h1>
                    </>
                ) : showFlow ? (
                    <Flow
                        reason={cancellationReason as keyof typeof Reasons}
                        subscriptionType={asPlanType(
                            currentPlan?.metadata?.plan_features
                        )}
                        subscriptionInterval={currentPlan.interval}
                    />
                ) : (
                    <>
                        <IonCard>
                            <IonItem lines="none">
                                <IonText className="text-small">
                                    {t("cancellation.whyCancelHeading")}
                                </IonText>
                            </IonItem>

                            <IonList>
                                <IonRadioGroup
                                    value={cancellationReason}
                                    onIonChange={(val) =>
                                        setCancellationReason(val.detail.value)
                                    }
                                >
                                    {Object.entries(Reasons).map(
                                        ([key, reason], index) => {
                                            return (
                                                <IonItem
                                                    lines="none"
                                                    key={index}
                                                >
                                                    <IonRadio
                                                        justify="space-between"
                                                        className="radio-button"
                                                        mode="ios"
                                                        data-testid={`cancel-subscription-reason-${index}`}
                                                        value={key}
                                                    >
                                                        {t(reason)}
                                                    </IonRadio>
                                                </IonItem>
                                            );
                                        }
                                    )}
                                </IonRadioGroup>
                            </IonList>
                        </IonCard>
                        <IonCard>
                            <IonItem lines="none">
                                <IonTextarea
                                    rows={5}
                                    data-testid="cancel-subscription-textarea"
                                    placeholder={t("cancellation.reasonText")}
                                    onIonInput={(e) =>
                                        setFeedbackText(e.detail.value!)
                                    }
                                ></IonTextarea>
                            </IonItem>
                        </IonCard>
                        <IonCard>
                            <IonButton
                                data-testid="continue-cancel-subscription-button"
                                disabled={handleDisableCancelButton}
                                expand="block"
                                onClick={cancelSubscriptionHandle}
                            >
                                <IonText>
                                    {t("cancellation.continueCancelling")}
                                </IonText>
                            </IonButton>
                        </IonCard>
                    </>
                )}
            </IonContent>
        </IonPage>
    );
};
